import { mapActions, mapGetters, mapState } from 'vuex'
import axios from 'axios'
import { recaptchaActions } from '@utils/appConstants'

const { VUE_APP_RECAPTCHA_V3_SITE_KEY } = process.env
const { VUE_APP_RECAPTCHA_V3_ENABLED } = process.env

// This axios interceptor will add the CSRF token to non-GET requests
axios.interceptors.request.use(
  async (config) => {
    try {
      // Fetch Token for ALL Non GET Requests
      if (config.method !== 'get')
        config.headers['X-CSRF-TOKEN'] = await getCsrfToken()

      return config
    } catch (error) {
      return Promise.reject(error)
    }
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const authComputed = {
  ...mapState('auth', {
    currentUser: (state) => state.currentUser,
  }),
  ...mapGetters('auth', ['loggedIn']),
}

export const customerComputed = {
  ...mapGetters('customer', ['hasCartProduct']),
}

export const authMethods = mapActions('auth', ['logIn', 'logOut'])

export const customerMethods = mapActions('customer', [
  'fetchUser',
  'register',
  'updateCustomer',
  'forgotPassword',
  'updateLockedAccountPassword',
  'registerBuybackProtection',
  'addToCart',
  'updateCart',
  'resetPassword',
  'resetLockedAccountPassword',
  'setBadCart',
])

export const orderMethods = mapActions('order', [
  'checkout',
  'checkoutPaypal',
  'checkoutPaypalSuccess',
  'validateVoucher',
  'checkoutVoucher',
])

export const vhrMethods = mapActions('vhr', [
  'getVinSummary',
  'getPlateSummary',
  'updateSummary',
  'getFullReport',
  'getVinFloodSummary',
  'getVinRecallSummary',
])

export const subscriptionMethods = mapActions('subscription', [
  'getSubscription',
  'setOwnership',
])

export const formsMethods = mapActions('forms', [
  'submitContact',
  'submitAffiliate',
])

export const chatMethods = mapActions('chat', ['initializeChat'])

export const referralMethods = mapActions('referrer', [
  'getData',
  'getProducts',
])

// ===
// Private helpers
// ===

export const getSavedState = (key) => {
  return JSON.parse(window.localStorage.getItem(key))
}

export const saveState = (key, state) => {
  window.localStorage.setItem(key, JSON.stringify(state))
}

export const isTokenValid = () => {
  const currentUser = JSON.parse(localStorage.getItem('auth.currentUser'))

  if (currentUser != null) {
    if (
      currentUser.tokenExpiration <= Date.now() ||
      !currentUser.token ||
      !currentUser.tokenExpiration
    )
      return false
    else return true
  } else return true
}

export const getParameterCaseInsensitive = (object, key) => {
  return object[
    Object.keys(object).find((k) => k.toLowerCase() === key.toLowerCase())
  ]
}

export const getRecaptchaV3Token = async (action) => {
  if (VUE_APP_RECAPTCHA_V3_ENABLED !== 'true') {
    return null
  }

  const timeout = 5000 // 5 seconds

  return ensureRecaptchaIsSet(timeout).then(async function() {
    try {
      return new Promise((resolve) => {
        window.grecaptcha.ready(async () => {
          const token = await window.grecaptcha.execute(
            VUE_APP_RECAPTCHA_V3_SITE_KEY,
            {
              action: action,
            }
          )
          return resolve(token)
        })
      })
    } catch (e) {
      return Promise.reject(e)
    }
  })
}

const getCsrfToken = async () => {
  try {
    // Call consumer services for CSRF Token
    const csrfTokenResponse = await axios.get(
      '/consumer-api/csrf-token/v1/token'
    )

    // Pull csrf token from response header and return
    return csrfTokenResponse.headers['x-csrf-token']
  } catch (error) {
    return Promise.reject(new Error('Failed to fetch CSRF token'))
  }
}

export const callToLoadAnalytics = async () => {
  if (VUE_APP_RECAPTCHA_V3_ENABLED !== 'true') {
    return true
  }

  const token = await getRecaptchaV3Token(recaptchaActions.loadAnalytics)

  return axios
    .get(`/consumer-api/meta/v1/should-load-analytics`, {
      headers: { recaptchaToken: token },
    })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      return Promise.reject(error)
    })
}

/**
 * recaptcha might not be loaded when we try to get the token on page load
 * this function will check for recaptcha every 30ms until it loads or
 * throw an error if it takes longer than the given timeout to load
 */
function ensureRecaptchaIsSet(timeout) {
  var start = Date.now()
  return new Promise(waitForRecaptcha)

  function waitForRecaptcha(resolve, reject) {
    if (window.grecaptcha) {
      resolve()
    } else if (timeout && Date.now() - start >= timeout) {
      reject(new Error('Recaptcha load timeout'))
    } else {
      setTimeout(waitForRecaptcha.bind(this, resolve, reject), 30)
    }
  }
}
